import { z } from 'zod';
import { SCENE_LIMITS } from '../scene/contracts/limits.ts';
import { jsonObjectSchema, jsonValueSchema } from './json.ts';

export const sceneSchemaVersion = '1.0.0' as const;

export const sceneIdentifierSchema = z.string()
  .min(1)
  .max(160)
  .regex(
    /^[a-z0-9][\w.:-]*$/i,
    '场景编号只能包含字母、数字、点、下划线、冒号和连字符',
  );

export const sceneTokenReferenceSchema = z.string().regex(
  /^\$[a-z][a-z0-9.-]*$/i,
  '主题变量必须使用 $token.path 格式',
);

export const sceneColorSchema = z.string().max(64).refine(
  value => (
    /^#[0-9a-f]{3,8}$/i.test(value)
    || /^rgba?\(\s*\d{1,3}(?:\.\d+)?%?\s*,\s*\d{1,3}(?:\.\d+)?%?\s*,\s*\d{1,3}(?:\.\d+)?%?(?:\s*,\s*(?:0|1|0?\.\d+))?\s*\)$/i.test(value)
    || /^(?:transparent|black|white|red|green|blue|gray|grey|orange|yellow|purple|pink|teal|cyan|indigo|currentcolor)$/i.test(value)
    || sceneTokenReferenceSchema.safeParse(value).success
  ),
  '颜色必须是安全颜色值或主题变量',
);

export const scenePointSchema = z.object({
  x: z.number().finite(),
  y: z.number().finite(),
}).strict();

export const sceneBoundsSchema = z.object({
  x: z.number().finite(),
  y: z.number().finite(),
  width: z.number().nonnegative().max(SCENE_LIMITS.maxWorldSize),
  height: z.number().nonnegative().max(SCENE_LIMITS.maxWorldSize),
}).strict();

export const sceneSemanticMetadataSchema = z.object({
  label: z.string().max(SCENE_LIMITS.maxTextLength).default(''),
  description: z.string().max(4000).default(''),
  role: z.string().max(100).default(''),
  tags: z.array(z.string().min(1).max(100)).max(100).default([]),
  conceptIds: z.array(z.string().min(1).max(160)).max(100).default([]),
  metadata: jsonObjectSchema.default({}),
}).strict();

export const visualIntentSchema = z.object({
  knowledge: z.string().max(4000).default(''),
  metaphor: z.string().max(2000).default(''),
  focus: z.array(z.string().min(1).max(500)).max(100).default([]),
  narrativeStages: z.array(z.object({
    id: sceneIdentifierSchema,
    title: z.string().min(1).max(500),
    purpose: z.string().max(2000).default(''),
  }).strict()).max(100).default([]),
  primaryObjects: z.array(z.string().min(1).max(500)).max(100).default([]),
  primaryTransitions: z.array(z.string().min(1).max(500)).max(100).default([]),
  learningGoals: z.array(z.string().min(1).max(1000)).max(100).default([]),
  rationale: z.string().max(4000).default(''),
  metadata: jsonObjectSchema.default({}),
}).strict();

export const sceneTransformSchema = z.object({
  x: z.number().finite().default(0),
  y: z.number().finite().default(0),
  scaleX: z.number().finite().min(-1000).max(1000).default(1),
  scaleY: z.number().finite().min(-1000).max(1000).default(1),
  rotation: z.number().finite().min(-360000).max(360000).default(0),
  pivotX: z.number().finite().default(0),
  pivotY: z.number().finite().default(0),
  skewX: z.number().finite().min(-89.9).max(89.9).default(0),
  skewY: z.number().finite().min(-89.9).max(89.9).default(0),
}).strict();

export const sceneTransformPatchSchema = sceneTransformSchema.partial().strict();

const scenePathDataSchema = z.string()
  .min(1)
  .max(SCENE_LIMITS.maxPathLength)
  .regex(
    /^[MLHVCSQTAZ0-9e+.,\-\s]+$/i,
    '路径只允许 SVG path 数值命令，不允许 SVG 文档',
  )
  .refine((value) => {
    const commands = value.match(/[MLHVCSQTAZ]/gi) ?? [];
    return commands.length <= SCENE_LIMITS.maxPathCommands;
  }, `路径命令不能超过 ${SCENE_LIMITS.maxPathCommands}`);

const sizeValue = z.number().nonnegative().max(SCENE_LIMITS.maxWorldSize);
const positiveSizeValue = z.number().positive().max(SCENE_LIMITS.maxWorldSize);

export const sceneGeometrySchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('rect'),
    width: positiveSizeValue,
    height: positiveSizeValue,
    radius: sizeValue.default(0),
  }).strict(),
  z.object({
    type: z.literal('circle'),
    radius: positiveSizeValue,
  }).strict(),
  z.object({
    type: z.literal('ellipse'),
    radiusX: positiveSizeValue,
    radiusY: positiveSizeValue,
  }).strict(),
  z.object({
    type: z.literal('line'),
    from: scenePointSchema,
    to: scenePointSchema,
  }).strict(),
  z.object({
    type: z.literal('polyline'),
    points: z.array(scenePointSchema).min(2).max(SCENE_LIMITS.maxPoints),
    closed: z.boolean().default(false),
  }).strict(),
  z.object({
    type: z.literal('polygon'),
    points: z.array(scenePointSchema).min(3).max(SCENE_LIMITS.maxPoints),
  }).strict(),
  z.object({
    type: z.literal('path'),
    data: scenePathDataSchema,
  }).strict(),
  z.object({
    type: z.literal('arc'),
    radius: positiveSizeValue,
    startAngle: z.number().finite(),
    endAngle: z.number().finite(),
    anticlockwise: z.boolean().default(false),
  }).strict(),
  z.object({
    type: z.literal('star'),
    points: z.number().int().min(3).max(100),
    outerRadius: positiveSizeValue,
    innerRadius: positiveSizeValue.optional(),
  }).strict(),
]);

export const sceneFilterSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('blur'),
    strength: z.number().min(0).max(32).default(4),
    quality: z.number().int().min(1).max(4).default(1),
  }).strict(),
  z.object({
    type: z.literal('color-matrix'),
    mode: z.enum([
      'brightness',
      'contrast',
      'saturate',
      'desaturate',
      'greyscale',
      'negative',
    ]),
    amount: z.number().min(-2).max(2).default(1),
  }).strict(),
]);

export const sceneStyleSchema = z.object({
  fill: sceneColorSchema.optional(),
  stroke: sceneColorSchema.optional(),
  strokeWidth: z.number().nonnegative().max(100).default(0),
  dash: z.array(z.number().positive().max(1000)).max(20).default([]),
  opacity: z.number().min(0).max(1).default(1),
  blendMode: z.enum([
    'normal',
    'add',
    'multiply',
    'screen',
    'darken',
    'lighten',
  ]).default('normal'),
  shadow: z.object({
    color: sceneColorSchema,
    alpha: z.number().min(0).max(1).default(0.25),
    blur: z.number().min(0).max(64).default(8),
    offsetX: z.number().finite().default(0),
    offsetY: z.number().finite().default(2),
  }).strict().optional(),
  filters: z.array(sceneFilterSchema).max(SCENE_LIMITS.maxFiltersPerNode).default([]),
}).strict();

export const sceneStylePatchSchema = sceneStyleSchema.partial().strict();

export const sceneTextStyleSchema = z.object({
  fontFamily: z.string().min(1).max(200).default('Noto Sans SC, sans-serif'),
  fontSize: z.number().positive().max(256).default(14),
  fontWeight: z.enum([
    'normal',
    'bold',
    '100',
    '200',
    '300',
    '400',
    '500',
    '600',
    '700',
    '800',
    '900',
  ]).default('400'),
  align: z.enum(['left', 'center', 'right']).default('left'),
  color: sceneColorSchema.default('#0f172a'),
  lineHeight: z.number().positive().max(1000).optional(),
  maxWidth: positiveSizeValue.optional(),
}).strict();

export const sceneTextStylePatchSchema = sceneTextStyleSchema.partial().strict();

export const sceneHitAreaSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('rect'),
    x: z.number().finite(),
    y: z.number().finite(),
    width: positiveSizeValue,
    height: positiveSizeValue,
  }).strict(),
  z.object({
    type: z.literal('circle'),
    x: z.number().finite(),
    y: z.number().finite(),
    radius: positiveSizeValue,
  }).strict(),
  z.object({
    type: z.literal('polygon'),
    points: z.array(scenePointSchema).min(3).max(1000),
  }).strict(),
]);

export const sceneTransitionEasingSchema = z.enum([
  'linear',
  'ease-in',
  'ease-out',
  'ease-in-out',
  'spring',
  'bounce',
]);

export const sceneTransitionPropertySchema = z.enum([
  'transform.x',
  'transform.y',
  'transform.scaleX',
  'transform.scaleY',
  'transform.rotation',
  'style.opacity',
  'style.fill',
  'style.stroke',
  'text.number',
]);

const sceneTransitionEndpointSchema = z.object({
  opacity: z.number().min(0).max(1).optional(),
  scale: z.number().min(0).max(100).optional(),
  offset: scenePointSchema.default({ x: 0, y: 0 }),
}).strict();

const sceneEnterExitTransitionSchema = z.object({
  durationMs: z.number().nonnegative().max(60000).default(0),
  easing: sceneTransitionEasingSchema.default('ease-out'),
  visual: sceneTransitionEndpointSchema.default({}),
}).strict();

export const sceneNodeTransitionSchema = z.object({
  enter: sceneEnterExitTransitionSchema.optional(),
  update: z.object({
    durationMs: z.number().nonnegative().max(60000).default(0),
    easing: sceneTransitionEasingSchema.default('ease-in-out'),
    properties: z.array(sceneTransitionPropertySchema)
      .min(1)
      .max(9)
      .default(['transform.x', 'transform.y']),
  }).strict().optional(),
  exit: sceneEnterExitTransitionSchema.optional(),
}).strict();

export const sceneNodeBaseSchema = z.object({
  id: sceneIdentifierSchema,
  parentId: sceneIdentifierSchema.optional(),
  name: z.string().max(500).default(''),
  transform: sceneTransformSchema.default({}),
  visible: z.boolean().default(true),
  zIndex: z.number().int().min(-100000).max(100000).default(0),
  styleRef: sceneIdentifierSchema.optional(),
  style: sceneStylePatchSchema.default({}),
  stateStyles: z.record(sceneIdentifierSchema, sceneStylePatchSchema).default({}),
  states: z.array(sceneIdentifierSchema).max(100).default([]),
  maskId: sceneIdentifierSchema.optional(),
  interactionIds: z.array(sceneIdentifierSchema).max(100).default([]),
  hitArea: sceneHitAreaSchema.optional(),
  transition: sceneNodeTransitionSchema.optional(),
  semantic: sceneSemanticMetadataSchema.default({}),
  accessibility: z.object({
    label: z.string().max(1000).default(''),
    description: z.string().max(4000).default(''),
    focusable: z.boolean().default(false),
    order: z.number().int().min(-100000).max(100000).default(0),
  }).strict().default({}),
  metadata: jsonObjectSchema.default({}),
}).strict();

const sceneNodeBaseShape = sceneNodeBaseSchema.shape;

export const sceneNodeSchema = z.discriminatedUnion('kind', [
  z.object({
    ...sceneNodeBaseShape,
    kind: z.literal('group'),
    sortable: z.boolean().default(true),
    clip: sceneBoundsSchema.optional(),
  }).strict(),
  z.object({
    ...sceneNodeBaseShape,
    kind: z.literal('shape'),
    geometry: sceneGeometrySchema,
  }).strict(),
  z.object({
    ...sceneNodeBaseShape,
    kind: z.literal('text'),
    text: z.string().max(SCENE_LIMITS.maxTextLength),
    textStyleRef: sceneIdentifierSchema.optional(),
    textStyle: sceneTextStylePatchSchema.default({}),
    anchorX: z.number().min(0).max(1).default(0),
    anchorY: z.number().min(0).max(1).default(0),
  }).strict(),
  z.object({
    ...sceneNodeBaseShape,
    kind: z.literal('sprite'),
    artifactId: sceneIdentifierSchema,
    frameArtifactIds: z.array(sceneIdentifierSchema)
      .max(SCENE_LIMITS.maxSpriteFrames)
      .default([]),
    frameRate: z.number().positive().max(120).default(12),
    width: positiveSizeValue.optional(),
    height: positiveSizeValue.optional(),
    fit: z.enum(['contain', 'cover', 'fill']).default('contain'),
    anchorX: z.number().min(0).max(1).default(0.5),
    anchorY: z.number().min(0).max(1).default(0.5),
  }).strict(),
  z.object({
    ...sceneNodeBaseShape,
    kind: z.literal('marker'),
    marker: z.enum(['arrow', 'dot', 'diamond', 'bar']),
    size: positiveSizeValue.default(10),
    angle: z.number().finite().default(0),
  }).strict(),
]);

export const sceneValueSourceSchema = z.discriminatedUnion('source', [
  z.object({
    source: z.literal('field'),
    field: z.string().min(1).max(500),
    fallback: jsonValueSchema.optional(),
  }).strict(),
  z.object({
    source: z.literal('literal'),
    value: jsonValueSchema,
  }).strict(),
  z.object({
    source: z.literal('scale'),
    scaleId: sceneIdentifierSchema,
    field: z.string().min(1).max(500),
    fallback: jsonValueSchema.optional(),
  }).strict(),
  z.object({
    source: z.literal('conditional'),
    field: z.string().min(1).max(500),
    cases: z.array(z.object({
      operator: z.enum([
        'eq',
        'neq',
        'gt',
        'gte',
        'lt',
        'lte',
        'in',
        'not-in',
        'exists',
        'contains',
      ]),
      value: jsonValueSchema.optional(),
      result: jsonValueSchema,
    }).strict()).min(1).max(100),
    fallback: jsonValueSchema.optional(),
  }).strict(),
]);

export const sceneDataFilterSchema = z.object({
  field: z.string().min(1).max(500),
  operator: z.enum([
    'eq',
    'neq',
    'gt',
    'gte',
    'lt',
    'lte',
    'in',
    'not-in',
    'exists',
    'contains',
  ]),
  value: jsonValueSchema.optional(),
}).strict();

export const sceneScaleSchema = z.object({
  id: sceneIdentifierSchema,
  type: z.enum(['linear', 'sqrt', 'log', 'ordinal', 'quantize', 'threshold']),
  domain: z.array(jsonValueSchema).min(1).max(1000),
  range: z.array(jsonValueSchema).min(1).max(1000),
  clamp: z.boolean().default(true),
  unknown: jsonValueSchema.optional(),
}).strict();

export const sceneAggregateOpSchema = z.discriminatedUnion('op', [
  // 计数：无需字段
  z.object({
    op: z.literal('count'),
    as: z.string().min(1).max(200),
  }).strict(),
  // 求和
  z.object({
    op: z.literal('sum'),
    field: z.string().min(1).max(500),
    as: z.string().min(1).max(200),
  }).strict(),
  // 均值
  z.object({
    op: z.literal('mean'),
    field: z.string().min(1).max(500),
    as: z.string().min(1).max(200),
  }).strict(),
  // 频率：本组计数 / 总行数（0~1）
  z.object({
    op: z.literal('frequency'),
    as: z.string().min(1).max(200),
  }).strict(),
  // 比值：sum(fieldA) / sum(fieldB)，可大于 1
  z.object({
    op: z.literal('ratio'),
    fieldA: z.string().min(1).max(500),
    fieldB: z.string().min(1).max(500),
    as: z.string().min(1).max(200),
  }).strict(),
  // 累计模式：运行期计数
  z.object({
    op: z.literal('cumulative-count'),
    as: z.string().min(1).max(200),
  }).strict(),
  // 累计模式：运行期频率（field == value 的累计数 / 累计总行数）
  z.object({
    op: z.literal('cumulative-frequency'),
    field: z.string().min(1).max(500),
    value: jsonValueSchema,
    as: z.string().min(1).max(200),
  }).strict(),
]);

export const sceneAggregateSchema = z.object({
  id: sceneIdentifierSchema,
  // group = 分组静态统计（默认）；cumulative = 累计运行序列
  mode: z.enum(['group', 'cumulative']).default('group'),
  groupBy: z.array(z.string().min(1).max(500)).max(20).default([]),
  filter: z.array(sceneDataFilterSchema).max(100).default([]),
  ops: z.array(sceneAggregateOpSchema).min(1).max(SCENE_LIMITS.maxAggregateOps),
}).strict();

export const sceneDataViewSchema = z.object({
  id: sceneIdentifierSchema,
  binding: z.string().min(1).max(500),
  select: z.array(z.string().min(1).max(500)).max(500).default([]),
  rename: z.record(z.string().min(1), z.string().min(1)).default({}),
  filters: z.array(sceneDataFilterSchema).max(100).default([]),
  sort: z.array(z.object({
    field: z.string().min(1).max(500),
    order: z.enum(['ascending', 'descending']),
  }).strict()).max(20).default([]),
  groupBy: z.array(z.string().min(1).max(500)).max(20).default([]),
  lookup: z.array(z.object({
    dataViewId: sceneIdentifierSchema,
    localField: z.string().min(1).max(500),
    foreignField: z.string().min(1).max(500),
    fields: z.array(z.string().min(1).max(500)).max(100).default([]),
    prefix: z.string().max(100).default('lookup.'),
  }).strict()).max(20).default([]),
  aggregate: z.array(sceneAggregateSchema).max(20).default([]),
  limit: z.number().int().positive().max(SCENE_LIMITS.maxRuntimeNodes).optional(),
  scales: z.array(sceneScaleSchema).max(100).default([]),
  format: z.record(z.string(), z.object({
    type: z.enum(['number', 'percent', 'integer', 'date', 'string']),
    precision: z.number().int().min(0).max(20).default(2),
  }).strict()).default({}),
}).strict();

export const sceneDefinitionSchema = z.object({
  id: sceneIdentifierSchema,
  nodes: z.array(sceneNodeSchema).min(1).max(1000),
  rootIds: z.array(sceneIdentifierSchema).min(1).max(1000),
  semantic: sceneSemanticMetadataSchema.default({}),
}).strict();

export const sceneInstanceSchema = z.object({
  id: sceneIdentifierSchema,
  definitionId: sceneIdentifierSchema,
  parentId: sceneIdentifierSchema.optional(),
  transform: sceneTransformPatchSchema.default({}),
  styleOverrides: z.record(sceneIdentifierSchema, sceneStylePatchSchema).default({}),
  textOverrides: z.record(sceneIdentifierSchema, z.string()
    .max(SCENE_LIMITS.maxTextLength)).default({}),
  states: z.array(sceneIdentifierSchema).max(100).default([]),
  semantic: sceneSemanticMetadataSchema.default({}),
}).strict();

export const sceneRepeatSchema = z.object({
  id: sceneIdentifierSchema,
  dataViewId: sceneIdentifierSchema,
  keyField: z.string().min(1).max(500),
  definitionId: sceneIdentifierSchema,
  parentId: sceneIdentifierSchema.optional(),
  mappings: z.record(z.string().min(1), sceneValueSourceSchema).default({}),
  filter: z.array(sceneDataFilterSchema).max(20).default([]),
  limit: z.number().int().positive().max(SCENE_LIMITS.maxRuntimeNodes).optional(),
  semantic: sceneSemanticMetadataSchema.default({}),
}).strict();

export const sceneLayoutSchema = z.object({
  id: sceneIdentifierSchema,
  capability: z.string().regex(
    /^[a-z0-9][a-z0-9.-]+@\d+$/,
    '布局能力必须使用版本化 key',
  ),
  containerId: sceneIdentifierSchema.optional(),
  targetIds: z.array(sceneIdentifierSchema).max(SCENE_LIMITS.maxRuntimeNodes).default([]),
  targetPrefix: z.string().max(200).optional(),
  config: jsonObjectSchema.default({}),
  priority: z.number().int().min(-1000).max(1000).default(0),
  responsive: z.array(z.object({
    maxWidth: z.number().positive().max(SCENE_LIMITS.maxWorldSize),
    capability: z.string().regex(
      /^[a-z0-9][a-z0-9.-]+@\d+$/,
      '响应式布局能力必须使用版本化 key',
    ).optional(),
    config: jsonObjectSchema.default({}),
  }).strict()).max(20).default([]),
}).strict();

export const sceneRelationSchema = z.object({
  id: sceneIdentifierSchema,
  type: z.enum([
    'connect',
    'align',
    'contain',
    'follow',
    'attach-label',
    'path',
    'clip',
    'associate',
    'relative',
  ]),
  sourceId: sceneIdentifierSchema,
  targetId: sceneIdentifierSchema,
  sourceAnchor: z.enum([
    'center',
    'north',
    'north-east',
    'east',
    'south-east',
    'south',
    'south-west',
    'west',
    'north-west',
  ]).default('center'),
  targetAnchor: z.enum([
    'center',
    'north',
    'north-east',
    'east',
    'south-east',
    'south',
    'south-west',
    'west',
    'north-west',
  ]).default('center'),
  route: z.enum(['straight', 'curve', 'polyline', 'orthogonal'])
    .default('straight'),
  markerStart: z.enum(['none', 'arrow', 'dot', 'diamond', 'bar'])
    .default('none'),
  markerEnd: z.enum(['none', 'arrow', 'dot', 'diamond', 'bar'])
    .default('none'),
  label: z.string().max(SCENE_LIMITS.maxTextLength).default(''),
  offset: scenePointSchema.default({ x: 0, y: 0 }),
  gap: z.number().nonnegative().max(SCENE_LIMITS.maxWorldSize).default(0),
  style: sceneStylePatchSchema.default({}),
  metadata: jsonObjectSchema.default({}),
}).strict();

export const sceneVisualStateSchema = z.object({
  id: sceneIdentifierSchema,
  priority: z.number().int().min(-10000).max(10000).default(0),
  source: z.enum([
    'data',
    'runtime',
    'hover',
    'selected',
    'focused',
    'active',
    'disabled',
    'dimmed',
    'highlighted',
    'error',
    'timeline',
    'custom',
  ]),
  style: sceneStyleSchema.default({}),
  transform: sceneTransformSchema.partial().optional(),
  visible: z.boolean().optional(),
  enterTimelineId: sceneIdentifierSchema.optional(),
  exitTimelineId: sceneIdentifierSchema.optional(),
}).strict();

const sceneTimelineStepBase = {
  id: sceneIdentifierSchema,
  delayMs: z.number().nonnegative().max(3600000).default(0),
};

export type SceneTimelineStepInput
  = | {
    id: string;
    type: 'sequence' | 'parallel';
    delayMs?: number;
    steps: SceneTimelineStepInput[];
  }
  | {
    id: string;
    type: 'stagger';
    delayMs?: number;
    intervalMs: number;
    steps: SceneTimelineStepInput[];
  }
  | {
    id: string;
    type: 'wait';
    delayMs?: number;
    durationMs: number;
  }
  | {
    id: string;
    type: 'tween';
    delayMs?: number;
    targetIds: string[];
    property: string;
    from?: unknown;
    to: unknown;
    durationMs: number;
    easing?: string;
  }
  | {
    id: string;
    type: 'state' | 'camera' | 'checkpoint' | 'trigger';
    delayMs?: number;
    targetIds?: string[];
    stateId?: string;
    cameraId?: string;
    name?: string;
    payload?: unknown;
  };

export const sceneTimelineStepSchema: z.ZodType<SceneTimelineStepInput>
  = z.lazy(() => z.discriminatedUnion('type', [
    z.object({
      ...sceneTimelineStepBase,
      type: z.literal('sequence'),
      steps: z.array(sceneTimelineStepSchema).min(1).max(1000),
    }).strict(),
    z.object({
      ...sceneTimelineStepBase,
      type: z.literal('parallel'),
      steps: z.array(sceneTimelineStepSchema).min(1).max(1000),
    }).strict(),
    z.object({
      ...sceneTimelineStepBase,
      type: z.literal('stagger'),
      intervalMs: z.number().nonnegative().max(3600000),
      steps: z.array(sceneTimelineStepSchema).min(1).max(1000),
    }).strict(),
    z.object({
      ...sceneTimelineStepBase,
      type: z.literal('wait'),
      durationMs: z.number().nonnegative().max(3600000),
    }).strict(),
    z.object({
      ...sceneTimelineStepBase,
      type: z.literal('tween'),
      targetIds: z.array(sceneIdentifierSchema).min(1).max(10000),
      property: z.enum([
        'transform.x',
        'transform.y',
        'transform.scaleX',
        'transform.scaleY',
        'transform.rotation',
        'style.opacity',
        'style.fill',
        'style.stroke',
        'geometry.progress',
        'text.number',
      ]),
      from: jsonValueSchema.optional(),
      to: jsonValueSchema,
      durationMs: z.number().positive().max(3600000),
      easing: z.enum([
        'linear',
        'ease-in',
        'ease-out',
        'ease-in-out',
        'spring',
        'bounce',
      ]).default('ease-in-out'),
    }).strict(),
    z.object({
      ...sceneTimelineStepBase,
      type: z.literal('state'),
      targetIds: z.array(sceneIdentifierSchema).min(1).max(10000),
      stateId: sceneIdentifierSchema,
    }).strict(),
    z.object({
      ...sceneTimelineStepBase,
      type: z.literal('camera'),
      cameraId: sceneIdentifierSchema,
    }).strict(),
    z.object({
      ...sceneTimelineStepBase,
      type: z.literal('checkpoint'),
      name: z.string().min(1).max(500),
    }).strict(),
    z.object({
      ...sceneTimelineStepBase,
      type: z.literal('trigger'),
      name: z.string().min(1).max(500),
      payload: jsonValueSchema.optional(),
    }).strict(),
  ]));

export const sceneTimelineSchema = z.object({
  id: sceneIdentifierSchema,
  root: sceneTimelineStepSchema,
  autoplay: z.boolean().default(false),
  loop: z.boolean().default(false),
  repeat: z.number().int().nonnegative().max(10000).default(0),
  playbackRate: z.number().positive().max(100).default(1),
  conflictPolicy: z.enum(['replace', 'queue', 'ignore']).default('replace'),
  cleanup: z.enum(['restore', 'commit', 'remove']).default('commit'),
  semantic: sceneSemanticMetadataSchema.default({}),
}).strict();

export const sceneInteractionConditionSchema = z.object({
  source: z.enum(['always', 'state', 'selection', 'field']),
  key: z.string().max(500).default(''),
  operator: z.enum(['eq', 'neq', 'contains', 'exists']).default('eq'),
  value: jsonValueSchema.optional(),
}).strict();

export const sceneInteractionActionSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('selection'),
    mode: z.enum(['replace', 'toggle', 'clear']).default('replace'),
    datasetId: z.string().min(1).optional(),
  }).strict(),
  z.object({
    type: z.literal('emit'),
    eventType: z.string().min(1).max(200),
    payload: jsonValueSchema.default({}),
  }).strict(),
  z.object({
    type: z.literal('runtime'),
    eventType: z.string().min(1).max(200),
    payload: jsonValueSchema.default({}),
  }).strict(),
  z.object({
    type: z.literal('timeline'),
    timelineId: sceneIdentifierSchema,
    command: z.enum([
      'play',
      'pause',
      'resume',
      'reset',
      'cancel',
      'seek',
      'step',
    ]),
    timeMs: z.number().nonnegative().max(3600000).optional(),
  }).strict(),
  z.object({
    type: z.literal('camera'),
    command: z.enum(['focus', 'follow', 'fit', 'reset', 'pan', 'zoom']),
    targetId: sceneIdentifierSchema.optional(),
    value: z.number().finite().optional(),
  }).strict(),
  z.object({
    type: z.literal('state'),
    stateId: sceneIdentifierSchema,
    mode: z.enum(['add', 'remove', 'toggle', 'replace']).default('toggle'),
    targetIds: z.array(sceneIdentifierSchema).max(10000).default([]),
  }).strict(),
]);

export const sceneInteractionSchema = z.object({
  id: sceneIdentifierSchema,
  event: z.enum([
    'pointerdown',
    'pointerup',
    'pointertap',
    'pointerover',
    'pointerout',
    'dragstart',
    'drag',
    'dragend',
    'wheel',
    'focus',
    'blur',
    'keydown',
    'brush',
  ]),
  propagation: z.enum(['bubble', 'capture', 'stop']).default('bubble'),
  hitPriority: z.number().int().min(-10000).max(10000).default(0),
  conditions: z.array(sceneInteractionConditionSchema).max(20).default([]),
  actions: z.array(sceneInteractionActionSchema).min(1).max(20),
  cursor: z.enum([
    'default',
    'pointer',
    'grab',
    'grabbing',
    'crosshair',
    'move',
    'not-allowed',
  ]).default('pointer'),
}).strict();

export const sceneCameraSchema = z.object({
  id: sceneIdentifierSchema,
  mode: z.enum([
    'fixed',
    'fit-scene',
    'fit-nodes',
    'focus',
    'follow',
    'landmark',
  ]).default('fit-scene'),
  x: z.number().finite().default(0),
  y: z.number().finite().default(0),
  zoom: z.number().positive().max(1000).default(1),
  targetIds: z.array(sceneIdentifierSchema).max(10000).default([]),
  landmarkId: sceneIdentifierSchema.optional(),
  minZoom: z.number().positive().max(1000).default(0.1),
  maxZoom: z.number().positive().max(1000).default(20),
  padding: z.number().nonnegative().max(10000).default(24),
  bounds: sceneBoundsSchema.optional(),
  interactive: z.object({
    pan: z.boolean().default(true),
    zoom: z.boolean().default(true),
    minZoom: z.number().positive().max(1000).default(0.1),
    maxZoom: z.number().positive().max(1000).default(20),
  }).strict().default({}),
}).strict().superRefine((camera, context) => {
  if (camera.minZoom > camera.maxZoom) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['minZoom'],
      message: '相机最小缩放不能大于最大缩放',
    });
  }
});

export const sceneLandmarkSchema = z.object({
  id: sceneIdentifierSchema,
  x: z.number().finite(),
  y: z.number().finite(),
  zoom: z.number().positive().max(1000).default(1),
}).strict();

export const sceneViewportSchema = z.object({
  width: z.number().positive().max(SCENE_LIMITS.maxWorldSize).default(800),
  height: z.number().positive().max(SCENE_LIMITS.maxWorldSize).default(450),
  background: sceneColorSchema.default('#ffffff'),
  coordinateSystem: z.enum(['cartesian', 'screen']).default('screen'),
  origin: scenePointSchema.default({ x: 0, y: 0 }),
  cameras: z.array(sceneCameraSchema).max(100).default([]),
  activeCameraId: sceneIdentifierSchema.optional(),
  landmarks: z.array(sceneLandmarkSchema).max(100).default([]),
  resize: z.enum(['contain', 'cover', 'stretch']).default('contain'),
}).strict();

export const sceneThemeSchema = z.object({
  id: sceneIdentifierSchema.default('default'),
  extends: sceneIdentifierSchema.optional(),
  colors: z.record(sceneIdentifierSchema, sceneColorSchema).default({
    primary: '#2563eb',
    secondary: '#0891b2',
    surface: '#ffffff',
    text: '#0f172a',
    muted: '#64748b',
    success: '#16a34a',
    warning: '#d97706',
    error: '#dc2626',
    selected: '#7c3aed',
  }),
  textStyles: z.record(sceneIdentifierSchema, sceneTextStyleSchema).default({}),
  styles: z.record(sceneIdentifierSchema, sceneStyleSchema).default({}),
}).strict();

export const sceneConstructInvocationSchema = z.object({
  id: sceneIdentifierSchema,
  capability: z.string().regex(/^[a-z0-9][a-z0-9.-]+@\d+$/),
  params: jsonObjectSchema.default({}),
  slots: z.record(sceneIdentifierSchema, z.array(sceneIdentifierSchema))
    .default({}),
  parentId: sceneIdentifierSchema.optional(),
  semantic: sceneSemanticMetadataSchema.default({}),
}).strict();

export const scenePerformanceSchema = z.object({
  maxRuntimeNodes: z.number().int().positive().max(SCENE_LIMITS.maxRuntimeNodes).default(10000),
  maxDevicePixelRatio: z.number().min(1).max(SCENE_LIMITS.maxDevicePixelRatio).default(SCENE_LIMITS.maxDevicePixelRatio),
  antialias: z.boolean().default(true),
  preference: z.enum(['webgl', 'webgpu']).default('webgl'),
  culling: z.boolean().default(true),
  reducedMotion: z.boolean().default(false),
}).strict();

export const sceneGrammarSchema = z.object({
  schemaVersion: z.literal(sceneSchemaVersion),
  id: sceneIdentifierSchema,
  revision: z.number().int().nonnegative().default(0),
  title: z.string().max(500).default(''),
  intent: visualIntentSchema.optional(),
  viewport: sceneViewportSchema.default({}),
  theme: sceneThemeSchema.default({}),
  dataViews: z.array(sceneDataViewSchema).max(SCENE_LIMITS.maxDataViews).default([]),
  definitions: z.array(sceneDefinitionSchema).max(SCENE_LIMITS.maxDefinitions).default([]),
  instances: z.array(sceneInstanceSchema).max(SCENE_LIMITS.maxRuntimeNodes).default([]),
  nodes: z.array(sceneNodeSchema).max(SCENE_LIMITS.maxNodes).default([]),
  repeats: z.array(sceneRepeatSchema).max(SCENE_LIMITS.maxRepeats).default([]),
  layouts: z.array(sceneLayoutSchema).max(SCENE_LIMITS.maxLayouts).default([]),
  relations: z.array(sceneRelationSchema).max(SCENE_LIMITS.maxRelations).default([]),
  states: z.array(sceneVisualStateSchema).max(SCENE_LIMITS.maxStates).default([]),
  timelines: z.array(sceneTimelineSchema).max(SCENE_LIMITS.maxTimelines).default([]),
  interactions: z.array(sceneInteractionSchema)
    .max(SCENE_LIMITS.maxInteractions)
    .default([]),
  constructs: z.array(sceneConstructInvocationSchema)
    .max(SCENE_LIMITS.maxConstructs)
    .default([]),
  performance: scenePerformanceSchema.default({}),
  semantic: sceneSemanticMetadataSchema.default({}),
  metadata: jsonObjectSchema.default({}),
}).strict().superRefine((scene, context) => {
  const collections: Array<[
    string,
    Array<{ id: string }>,
  ]> = [
    ['dataViews', scene.dataViews],
    ['definitions', scene.definitions],
    ['instances', scene.instances],
    ['nodes', scene.nodes],
    ['repeats', scene.repeats],
    ['layouts', scene.layouts],
    ['relations', scene.relations],
    ['states', scene.states],
    ['timelines', scene.timelines],
    ['interactions', scene.interactions],
    ['constructs', scene.constructs],
    ['viewport.cameras', scene.viewport.cameras],
    ['viewport.landmarks', scene.viewport.landmarks],
  ];
  for (const [collectionName, values] of collections) {
    const ids = new Set<string>();
    for (const [index, value] of values.entries()) {
      if (ids.has(value.id)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...collectionName.split('.'), index, 'id'],
          message: `${collectionName} 存在重复编号：${value.id}`,
        });
      }
      ids.add(value.id);
    }
  }

  const nodeIds = new Set(scene.nodes.map(node => node.id));
  for (const [index, node] of scene.nodes.entries()) {
    if (node.parentId && !nodeIds.has(node.parentId)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['nodes', index, 'parentId'],
        message: `节点父级不存在：${node.parentId}`,
      });
    }
    if (node.parentId === node.id) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['nodes', index, 'parentId'],
        message: '节点不能以自身作为父级',
      });
    }
    if (node.kind === 'group' && node.clip && node.maskId) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['nodes', index, 'clip'],
        message: 'group 的 clip 与显式 maskId 不能同时使用',
      });
    }
  }
  for (const [definitionIndex, definition] of scene.definitions.entries()) {
    for (const [nodeIndex, node] of definition.nodes.entries()) {
      if (node.kind === 'group' && node.clip && node.maskId) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [
            'definitions',
            definitionIndex,
            'nodes',
            nodeIndex,
            'clip',
          ],
          message: 'group 的 clip 与显式 maskId 不能同时使用',
        });
      }
    }
  }

  const activeCameraId = scene.viewport.activeCameraId;
  if (
    activeCameraId
    && !scene.viewport.cameras.some(camera => camera.id === activeCameraId)
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['viewport', 'activeCameraId'],
      message: `活动相机不存在：${activeCameraId}`,
    });
  }
});

export type ScenePoint = z.infer<typeof scenePointSchema>;
export type SceneBounds = z.infer<typeof sceneBoundsSchema>;
export type SceneTransform = z.infer<typeof sceneTransformSchema>;
export type SceneGeometry = z.infer<typeof sceneGeometrySchema>;
export type SceneStyle = z.infer<typeof sceneStyleSchema>;
export type SceneTextStyle = z.infer<typeof sceneTextStyleSchema>;
export type SceneHitArea = z.infer<typeof sceneHitAreaSchema>;
export type SceneNodeTransition = z.infer<typeof sceneNodeTransitionSchema>;
export type SceneTransitionProperty = z.infer<
  typeof sceneTransitionPropertySchema
>;
export type SceneNode = z.infer<typeof sceneNodeSchema>;
export type SceneDataView = z.infer<typeof sceneDataViewSchema>;
export type SceneAggregateOp = z.infer<typeof sceneAggregateOpSchema>;
export type SceneAggregate = z.infer<typeof sceneAggregateSchema>;
export type SceneDefinition = z.infer<typeof sceneDefinitionSchema>;
export type SceneInstance = z.infer<typeof sceneInstanceSchema>;
export type SceneRepeat = z.infer<typeof sceneRepeatSchema>;
export type SceneLayout = z.infer<typeof sceneLayoutSchema>;
export type SceneRelation = z.infer<typeof sceneRelationSchema>;
export type SceneVisualState = z.infer<typeof sceneVisualStateSchema>;
export type SceneTimeline = z.infer<typeof sceneTimelineSchema>;
export type SceneTimelineStep = z.infer<typeof sceneTimelineStepSchema>;
export type SceneInteraction = z.infer<typeof sceneInteractionSchema>;
export type SceneInteractionAction = z.infer<
  typeof sceneInteractionActionSchema
>;
export type SceneCamera = z.infer<typeof sceneCameraSchema>;
export type SceneViewport = z.infer<typeof sceneViewportSchema>;
export type SceneTheme = z.infer<typeof sceneThemeSchema>;
export type ScenePerformance = z.infer<typeof scenePerformanceSchema>;
export type SceneConstructInvocation = z.infer<
  typeof sceneConstructInvocationSchema
>;
export type SceneGrammar = z.infer<typeof sceneGrammarSchema>;
export type VisualIntent = z.infer<typeof visualIntentSchema>;
